import React, { useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import Label from "../../../../components/Label/Label";
import "./BatchFilterPanel.scss";
import { validateBatchFilters } from "./BatchFiltersValidation";
import { plotTypeOptions } from "../constants";
import { useDispatch, useSelector } from "react-redux";
import {
  clearFilteredBatchList,
  clearParameterList,
  fetchFilteredBatchList,
  fetchParameterList,
} from "../../../../redux/visualization/actions";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const BatchFilterPanel = ({
  drugProductId,
  siteId,
  unitOfOperationId,
  updateState,
  setPreviewData,
  setShowPreview,
  onClickClearAllFilters,
  handleGenerate,
  batchState,
  disableAllFields,
  onClickPreview,
  parameterState,
  setShowConfigPanel,
  setSelectedTab,
}) => {
  const dispatch = useDispatch();

  const {
    product = { id: "", name: "" },
    site = { id: "", name: "" },
    unitOfOperation = { id: "", name: "" },
  } = useSelector((state) => state.shared.productSitesUopValues || {});

  // const [configErrors, setConfigErrors] = useState({});
  // const [batchErrors, setBatchErrors] = useState({});
  const {
    data: parameterData,
    loader: parameterLoader,
    apiError: parameterError,
    message: parameterMessage,
    apiSuccess: parameterSuccess,
  } = useSelector((state) => state.viz.parameterNamesList || {});

  // const {
  //   data: batchData,
  //   loader: batchLoader,
  //   apiError: batchError,
  //   message: batchMessage,
  //   apiSuccess: batchSuccess,
  // } = useSelector((state) => state.viz.filteredBatchList || {});

  useEffect(() => {
    if (product.id || site.id) {
      // Clear parameter list & batch list
      dispatch(clearFilteredBatchList());
      dispatch(clearParameterList());
    }
  }, [product.id, site.id, dispatch]);

  // useEffect(() => {
  //   dispatch(clearFilteredBatchList());
  //   dispatch(clearParameterList());
  //   onClickClearAllFilters();
  // }, [product, site]);

  useEffect(() => {
    if (product.id && site.id && unitOfOperation.id) {
      const payload = {
        drug_product_id: product.id,
        site_id: site.id,
        unit_op_id: unitOfOperation.id,
      };
      dispatch(fetchParameterList(payload));
    }
  }, [product.id, site.id, unitOfOperation.id, dispatch]);

  // useEffect(() => {
  //   if (Array.isArray(batchData)) {
  //     setPreviewData(batchData);
  //   } else {
  //     setPreviewData([]);
  //   }
  // }, [batchData]);

  // useEffect(() => {
  //   if (Array.isArray(parameterData)) {
  //     setParameterData(parameterData);
  //   } else {
  //     setParameterData([]);
  //   }
  // }, [parameterData]);

  useEffect(() => {
    return () => {
      dispatch(clearFilteredBatchList());
      dispatch(clearParameterList());
    };
  }, []);

  const handleRadioChange = (e) => {
    const selected = e.target.value;
    updateState("batchRangeOption", selected);

    const resetMap = {
      by_latest_x: ["recentDayCount", "startDate", "endDate"],
      by_recent_x_days: ["recentBatchCount", "startDate", "endDate"],
      by_date_x: ["recentBatchCount", "recentDayCount"],
    };

    resetMap[selected]?.forEach((field) =>
      updateState(field, field.includes("Date") ? null : ""),
    );

    updateState("batchIdList", []);
    dispatch(clearFilteredBatchList());
    setPreviewData([]);
    setShowPreview(false);
  };

  // const handleGenerateClick = () => {
  // const batchErrors = validateBatchFilters(
  //   plotType,
  //   parameter,
  //   batchRangeOption,
  //   recentBatchCount,
  //   recentDayCount,
  //   startDate,
  //   endDate
  // );
  // if (
  //   Object.keys(batchErrors).length > 0 ||
  //   Object.keys(configErrors).length > 0
  // ) {
  //   setBatchErrors(batchErrors);
  //   return; // Don’t proceed
  // }
  // Proceed with API call or plot rendering
  // if (Object.keys(batchErrors).length === 0 && drugProductId) {
  // All valid, proceed to API
  // saveConfigToAPI(); // or your function to send data
  // }
  // };

  const isDisabledPreview = (
    drugProductId,
    siteId,
    unitOfOperationId,
    batchState,
  ) => {
    if (!drugProductId) return true;
    if (!siteId) return true;
    if (!unitOfOperationId) return true;

    const {
      batchRangeOption,
      recentBatchCount,
      recentDayCount,
      startDate,
      endDate,
    } = batchState || {};

    if (batchRangeOption === "by_latest_x" && !recentBatchCount) return true;
    if (batchRangeOption === "by_recent_x_days" && !recentDayCount) return true;
    if (batchRangeOption === "by_date_x" && (!startDate || !endDate))
      return true;

    return false;
  };

  const isDisabled = (
    drugProductId,
    siteId,
    unitOfOperationId,
    batchState,
    parameterState,
  ) => {
    if (!drugProductId) return true;
    if (!siteId) return true;
    if (!unitOfOperationId) return true;
    if (!parameterState?.plotType) return true;
    if (!parameterState?.parameter.label?.trim()) return true;

    const {
      batchRangeOption,
      recentBatchCount,
      recentDayCount,
      startDate,
      endDate,
    } = batchState || {};

    if (batchRangeOption === "by_latest_x" && !recentBatchCount) return true;
    if (batchRangeOption === "by_recent_x_days" && !recentDayCount) return true;
    if (batchRangeOption === "by_date_x" && (!startDate || !endDate))
      return true;

    return false;
  };

  return (
    <Box className="batch-filter-panel">
      <Box className="scrollable-content">
        {/* Parameter Selection */}
        {/* <Card elevation={0} className="filter-card">
          <CardContent>
            <Label text="Parameter" fontWeight="500" />
            <FormControl
              fullWidth
              size="small"
              className="dropdown-field"
              disabled={disableAllFields}
            >
              <InputLabel>Select Parameter</InputLabel>
              <Select
                value={batchState?.parameter?.parameterId || ""} // so it matches MenuItem value
                onChange={(e) => {
                  const selectedParam = parameterData?.find(
                    (p) => p.parameter_id === e.target.value,
                  );
                  updateState("parameter", {
                    label: selectedParam.label,
                    parameterId: selectedParam.parameter_id,
                    parameterName: selectedParam.parameter_name,
                  }); // store whole object
                }}
                label="Select Parameter"
              >
                {parameterData?.map((param) => (
                  <MenuItem key={param.parameter_id} value={param.parameter_id}>
                    {param.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card> */}

        {/* Plot Type */}
        {/* <Card elevation={0} className="filter-card">
          <CardContent>
            <Label text="Plot Type" fontWeight="500" />
            <FormControl
              fullWidth
              size="small"
              className="dropdown-field"
              disabled={disableAllFields}
            >
              <InputLabel>Plot Type</InputLabel>
              <Select
                value={batchState?.plotType}
                onChange={(e) => updateState("plotType", e.target.value)}
                label="Plot Type"
              >
                {plotTypeOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card> */}

        {/* Batch Range */}
        <Card elevation={0} className="filter-card batch-range-card">
          <CardContent>
            <Label text="Batch Range" fontWeight="500" />

            <RadioGroup
              value={batchState?.batchRangeOption}
              onChange={handleRadioChange}
              className="batch-range-options"
            >
              <FormControlLabel
                disabled={disableAllFields}
                value="by_latest_x"
                control={<Radio size="small" />}
                className="batch-range-radio"
                label={
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    placeholder="Recent Batches"
                    value={batchState?.recentBatchCount}
                    onChange={(e) =>
                      updateState("recentBatchCount", e.target.value)
                    }
                    disabled={
                      batchState?.batchRangeOption !== "by_latest_x" ||
                      disableAllFields
                    }
                  />
                }
              />

              <Divider className="batch-section-divider" />

              <FormControlLabel
                disabled={disableAllFields}
                value="by_recent_x_days"
                control={<Radio size="small" />}
                className="batch-range-radio"
                label={
                  <TextField
                    fullWidth
                    type="number"
                    size="small"
                    placeholder="Recent Days"
                    value={batchState?.recentDayCount}
                    onChange={(e) =>
                      updateState("recentDayCount", e.target.value)
                    }
                    disabled={
                      batchState?.batchRangeOption !== "by_recent_x_days" ||
                      disableAllFields
                    }
                  />
                }
              />

              <Divider className="batch-section-divider" />

              <FormControlLabel
                disabled={disableAllFields}
                value="by_date_x"
                control={<Radio size="small" />}
                className="batch-range-radio"
                label={
                  <Box className="date-range-picker">
                    {[
                      ["startDate", "From"],
                      ["endDate", "To"],
                    ].map(([key, placeholder]) => (
                      <DatePicker
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={50}
                        popperClassName="custom-datepicker-popper"
                        key={key}
                        selected={
                          key === "startDate"
                            ? batchState?.startDate instanceof Date
                              ? batchState?.startDate
                              : null
                            : batchState?.endDate instanceof Date
                              ? batchState?.endDate
                              : null
                        }
                        onChange={(date) => updateState(key, date)}
                        selectsStart={key === "startDate"}
                        selectsEnd={key === "endDate"}
                        startDate={
                          batchState?.startDate instanceof Date
                            ? batchState?.startDate
                            : null
                        }
                        endDate={
                          batchState?.endDate instanceof Date
                            ? batchState?.endDate
                            : null
                        }
                        minDate={
                          key === "endDate" &&
                          batchState?.startDate instanceof Date
                            ? batchState?.startDate
                            : undefined
                        }
                        maxDate={
                          key === "startDate"
                            ? batchState?.endDate instanceof Date
                              ? batchState?.endDate
                              : new Date()
                            : new Date()
                        }
                        placeholderText={placeholder}
                        disabled={
                          batchState?.batchRangeOption !== "by_date_x" ||
                          disableAllFields
                        }
                        dateFormat="dd-MMM-yyyy"
                        fixedHeight
                        renderCustomHeader={({
                          date,
                          decreaseMonth,
                          increaseMonth,
                          changeYear,
                          prevMonthButtonDisabled,
                          nextMonthButtonDisabled,
                        }) => (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 1.5,
                              py: 1,
                              bgcolor: "primary.main",
                              color: "common.white",
                              borderRadius: "8px 8px 0 0",
                            }}
                          >
                            <IconButton
                              onClick={decreaseMonth}
                              disabled={prevMonthButtonDisabled}
                              sx={{ color: "common.white", p: 0.5 }}
                              size="small"
                            >
                              <ArrowBackIosIcon fontSize="small" />
                            </IconButton>

                            <Box
                              sx={{
                                mx: 1,
                                fontWeight: 500,
                                cursor: "default",
                                width: 80,
                                textAlign: "center",
                              }}
                            >
                              <Label
                                text={date.toLocaleString("default", {
                                  month: "long",
                                })}
                                color="#ffff"
                                fontSize={16}
                                fontWeight={600}
                              />
                            </Box>

                            <Select
                              value={date.getFullYear()}
                              onChange={(e) =>
                                changeYear(Number(e.target.value))
                              }
                              size="small"
                              onMouseDown={(e) => e.stopPropagation()} // prevent closing
                              onClick={(e) => e.stopPropagation()} // prevent closing
                              sx={{
                                fontSize: 13,
                                color: "text.primary",
                                bgcolor: "background.paper",
                                borderRadius: 1,
                                // "& .MuiSelect-select": { py: 0.25, px: 1 },
                              }}
                            >
                              {Array.from(
                                { length: 50 },
                                (_, i) => new Date().getFullYear() - i,
                              ).map((y) => (
                                <MenuItem key={y} value={y}>
                                  {y}
                                </MenuItem>
                              ))}
                            </Select>

                            <IconButton
                              onClick={increaseMonth}
                              disabled={nextMonthButtonDisabled}
                              sx={{ color: "common.white", p: 0.5 }}
                              size="small"
                            >
                              <ArrowForwardIosIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        customInput={
                          <TextField
                            size="small"
                            fullWidth
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CalendarTodayIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        }
                      />
                    ))}
                  </Box>
                }
              />

              <Divider className="batch-section-divider" />
            </RadioGroup>

            <Box className="preview-button-wrapper">
              <Button
                disabled={isDisabledPreview(
                  drugProductId,
                  siteId,
                  unitOfOperationId,
                  batchState,
                )}
                variant="contained"
                startIcon={<PreviewOutlinedIcon />}
                // size="small"
                onClick={() => {
                  onClickPreview();
                }}
              >
                Preview
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Sticky Footer */}
      <Box className=" footer-sticky ">
        <Card elevation={0} className=" filter-card plot-footer-btn-card">
          <CardContent>
            <Box className="footer-btn-wrapper">
              <Tooltip title="Previous">
                <IconButton
                  onClick={() => setSelectedTab("parameter")}
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": { backgroundColor: "primary.dark" },
                    borderRadius: "8px",
                    px: 1,
                    py: 0.6,
                    mr: "auto",
                  }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Button
                size="small"
                variant="outlined"
                className="close-btn"
                onClick={() => setShowConfigPanel(false)}
              >
                Close
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={onClickClearAllFilters}
                disabled={disableAllFields}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                disabled={
                  isDisabled(
                    drugProductId,
                    siteId,
                    unitOfOperationId,
                    batchState,
                    parameterState,
                  ) || disableAllFields
                }
                onClick={handleGenerate}
              >
                Generate
              </Button>

              <Tooltip title="Next">
                <IconButton
                  onClick={() => setSelectedTab("configuration")}
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                    borderRadius: "8px",
                    px: 1,
                    py: 0.6,
                  }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default BatchFilterPanel;
